import './EsHomeHero.css';

import escapadeLogoMug from '../../images/escapadeLogoMug.png';
import escapadeLogoWordDM from '../../images/escapadeLogoWordDM.png';
import escapadeLogoWordLM from '../../images/escapadeLogoWordLM.png';
import miniHumanFemaleWizard from '../../images/miniHumanFemaleWizard.png';
import { VIEW_REGISTER } from '../../common/EsConstants';
import { esCopyTag } from '../../common/EsCopy';
import { useEsStrings } from '../../common/EsStrings';
import EsHomeVideo from './EsHomeVideo';

/* The pen cup and the wordmark in place of the text title (Tim, 2026-10-11), built as
   EsFooter builds them. Heights keep the footer's mug-to-name ratio, 42:27; at the images'
   aspects (mug 303x290, name 621x127) the pair is ~214px wide on a phone, which a 320px
   screen's 256px column holds, and ~419px from md (a 96px mug), inside the hero's 568px. The images
   are alt="" and the name is sr-only text, so a reader hears it once and AQA's getText
   still reads it. EsBlockHero draws this for the Crafter home page. */
export function EsHeroLogo( { title, fluid = false } ) {
    // fluid: the home hero's, sized by EsHeroRow's --hero-mug rather than stepping at md.
    const aMug = fluid ? "h-(--hero-mug)" : "h-12 md:h-24";
    const aWord = fluid ? "h-[calc(var(--hero-mug)*0.646)]" : "h-[31px] md:h-[62px]";
    return (
        <>
            <img src={ escapadeLogoMug } alt="" className={ `${ aMug } w-auto` } />
            <img src={ escapadeLogoWordLM } alt="" className={ `${ aWord } w-auto dark:hidden` } />
            <img src={ escapadeLogoWordDM } alt="" className={ `hidden ${ aWord } w-auto dark:block` } />
            <span className="sr-only">{ title }</span>
        </>
    );
}

/* The price, under the mini (Tim, 2026-10-31), stamped into the page: the pen cup's orange
   (#e5733e, its middle tone; its lit side #f0a860 for the faint edge), small, with an inner shadow and text
   pressed in rather than a sticker standing off it. Turned 6 degrees about its top-right
   corner, which sits level with the buttons' top. */
export function EsHeroPrice() {
    const t = useEsStrings();
    return (
        <p data-testid="es-home-hero-price" className="origin-top-right -rotate-6 whitespace-nowrap rounded-lg border border-[#f0a860]/25 bg-[#e5733e]/55 px-2 py-1 text-center heroPrice text-xs leading-relaxed text-orange-50/90 shadow-[inset_0_1px_3px_rgba(0,0,0,0.55),inset_0_-1px_0_rgba(240,168,96,0.15)] [text-shadow:0_-1px_0_rgba(0,0,0,0.45)] max-sm:text-[clamp(10px,1.85vw,12px)]">
            <span className="block">{ t( 'home-hero-d20-players', 'Players free.' ) }</span>
            <span className="block">{ t( 'home-hero-d20-gms', 'GMs $35 a year.' ) }</span>
        </p>
    );
}

/* The home hero's row (Tim, 2026-10-31): the mini, facing right, then the pitch, then the video,
   64px apart. The hairline is as far below the video as the nav is above it: from xl, the
   top padding less the 74px nav, less the features' 50px; below xl, the 64px the video stacks at.
   From xl the pitch runs the video's height: logo at its top, buttons at its bottom, and the
   blurb, justified, between them.
   Below xl the mini and pitch keep their row and the video goes under them. Decorative, so the
   mini is alt="". EsBlockHero draws the same row for the Crafter home page.

   ONE SIZE DRIVES IT ALL, --hero-mug, the logo mug's height: 12.5vw, so the logo keeps its xl
   size down to 768px and shrinks only as the column must, to 48px at a phone. The wordmark, the mini (twice the mug), and the gap between mini and
   pitch follow it, so nothing steps at a breakpoint.

   EACH SENTENCE OF THE BLURB KEEPS ITS OWN LINE at every width in English (Tim, 2026-10-31;
   another language wraps as a paragraph, T103). Below xl the
   blurb is a size container and its text is min(its size, 3.7cqw), the size at which the longer
   sentence just fills the column, floored at 12px. Below sm the buttons and price scale with
   the width (their sm sizes times vw/649) so the buttons keep one row, floored at 11px.

   ONE GRID AT EVERY WIDTH: the mini's column, then the pitch's, up to 600px. The pitch's box is
   display:contents, so its logo, blurb, and buttons are the grid's rows, 40px apart at every
   width (gap-y-10, their own margins zeroed). From xl the pitch's
   column is only as wide as its longest line (fit-content), as the flex column it replaced was,
   so the justified blurb never stretches its words; and the rows spread
   (content-between) over the video's height. From sm the mini hangs from the top, dropped 0.43
   of a mug, which puts her outstretched hand (0.137 of her height down) level with the cup, 0.7
   of the way down the mug. Below sm she sits in the blurb's row, drawn from its bottom, so her
   base lines up with the last line of text; absolute
   there, so she never sets a row's height. The fade sits on the grid, since a contents box
   has no rect for WOW to watch. */
export function EsHeroRow( { mini, video, price, children } ) {
    return (
        <div className="container">
            <div className="flex flex-col items-center gap-16 xl:flex-row xl:items-stretch xl:justify-center xl:gap-16">
                { /* No mini (another Crafter page's hero): an empty first column with no gap. */ }
                <div className={ `grid w-full justify-center [--hero-mug:clamp(3rem,12.5vw,6rem)] wow fadeInUp xl:w-auto xl:shrink-0 xl:content-between ${ mini ? 'grid-cols-[auto_minmax(0,600px)] gap-x-[calc(var(--hero-mug)*0.67)] gap-y-10 xl:grid-cols-[auto_fit-content(600px)]' : 'grid-cols-[0_minmax(0,600px)] xl:grid-cols-[0_fit-content(600px)]' }` } data-wow-delay=".2s">
                    { mini && (
                        <div className="relative col-start-1 row-start-2 w-[calc(var(--hero-mug)*1.594)] justify-self-center sm:row-span-2 sm:row-start-1 sm:self-start sm:pt-[calc(var(--hero-mug)*0.43)]">
                            <div className="absolute bottom-0 left-0 sm:relative">
                                <img src={ mini } alt="" className="h-[calc(var(--hero-mug)*2)] w-auto max-w-none -scale-x-100 opacity-60" />
                            </div>
                        </div>
                    ) }
                    { /* Under her, from the buttons' top, centered on her base: 9% of her width right of
                         her box's center, since the staff hand reaches left. */ }
                    { mini && price && (
                        <div className="col-start-1 row-start-3 translate-x-[calc(var(--hero-mug)*0.143)] self-start justify-self-center">
                            <EsHeroPrice />
                        </div>
                    ) }
                    <div className={ `contents ${ mini ? 'text-left xl:text-center' : 'text-center' }` }>
                        { children }
                    </div>
                </div>
                { video && <EsHomeVideo inline /> }
            </div>
        </div>
    );
}

// The blurb's one-sentence-a-line layout is measured on the English (T103): the break, the
// shrink to fit the longer sentence, and the justified last line. Another language's sentences
// run longer or shorter, so its blurb wraps as a paragraph does. No tag is English, as in EsDate.
const englishTag = () => {
    const aTag = esCopyTag();
    return !aTag || aTag.toLowerCase().startsWith( 'en' );
};

export default function EsHomeHero() {
    const t = useEsStrings();
    const aEnglish = englishTag();
    return (
        <section id="home" data-testid="es-home-hero" className="relative z-10 overflow-hidden pt-[120px] pb-[14px] md:pt-[150px] xl:pt-[180px] xl:pb-[56px] 2xl:pt-[210px] 2xl:pb-[86px]">
            <EsHeroRow mini={ miniHumanFemaleWizard } video price>
                <h1 data-testid="es-home-hero-title" className="heroLogo col-start-2 row-start-1 mb-0 justify-start xl:justify-center">{ /* es-copy: literal -- the brand */ }<EsHeroLogo title="Escapade" fluid /></h1>
                <p data-testid="es-home-hero-blurb" className={ `heroBlurb col-start-2 row-start-2 mb-0 ${ aEnglish ? 'max-xl:@container xl:text-justify xl:[text-align-last:justify]' : '' }` }>
                    { /* A space before the break, so the text reads "5E. Play" and
                         matches the Crafter field, which holds both sentences in one
                         string and cannot break a line. */ }
                    <span className={ aEnglish ? 'max-xl:text-[max(12px,min(1em,3.7cqw))]' : undefined }>{ t( 'home-hero-blurb-one', 'Streamline role-playing games like D&D and Pathfinder.' ) }{ ' ' }
                    { aEnglish && <br className="max-sm:hidden" /> }
                    { t( 'home-hero-blurb-two', 'Play with dice face-to-face or through virtual tabletop.' ) }</span>
                </p>
                <div className="heroButtons col-start-2 row-start-3 self-start flex-wrap justify-start gap-4 max-sm:gap-[2.465vw] xl:justify-between">
                    <a href={ VIEW_REGISTER }
                       data-testid="es-home-hero-features-button"
                       className="buttonPrimary mx-0! whitespace-nowrap px-4! md:px-8! max-sm:px-[clamp(8px,2.465vw,16px)]! max-sm:py-[clamp(8px,1.85vw,12px)]! max-sm:text-[clamp(11px,2.157vw,14px)]!">
                        { t( 'home-hero-building', 'Start Your Free Trial' ) }
                    </a>
                    <a href="/#principles"
                       data-testid="es-home-hero-principles-button"
                       className="buttonSecondary mx-0! whitespace-nowrap px-4! md:px-8! max-sm:px-[clamp(8px,2.465vw,16px)]! max-sm:py-[clamp(8px,1.85vw,12px)]! max-sm:text-[clamp(11px,2.157vw,14px)]!">
                        { t( 'home-hero-trust', 'Why You Can Trust Us' ) }
                    </a>
                </div>
            </EsHeroRow>
            <div className="absolute bottom-0 left-0 z-[-1]">
            </div>
        </section>
    );
}
